import * as React from 'react';
import type { DisplayDate } from 'owa-datetime';
import { AgendaEventViewBase, Detail, AgendaEventViewBaseProps } from 'owa-agenda-event-views';
import { getDurationString } from 'owa-agenda-events-common';
import { formatUserTime } from '../selectors/dateTimeSelectors';
import { observer } from 'mobx-react-lite';
import { getCalendarEventIcons } from 'owa-calendar-event-icons';
import { getPalette } from 'owa-theme';
import { CharmControlColorPattern, CharmControlColorProps } from 'owa-charmcontrol';
import { getDensityModeString } from 'owa-fabric-theme';

import styles from './common.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface NormalProps {
    startTime: DisplayDate;
    endTime: DisplayDate;
    organizerEmail: string;
    organizerName: string;
    location: string;
    subject: string;
    isCancelled: boolean;
    isMeeting: boolean;
    hasAttachments: boolean;
    isRecurring: boolean;
    isPrivate: boolean;
    isRecurringException: boolean;
    charmId: number;
}

export type AgendaEventViewNormalProps = Omit<
    AgendaEventViewBaseProps,
    'topDetails' | 'bottomDetails'
> &
    NormalProps;

function withNormalView(
    AgendaEventViewBase: React.ComponentType<AgendaEventViewBaseProps>
): React.ComponentType<AgendaEventViewNormalProps & NormalProps> {
    const AgendaEventViewNormal = observer(function (props: AgendaEventViewNormalProps) {
        const {
            startTime,
            endTime,
            isCancelled,
            subject,
            location,
            className,
            organizerName,
            isMeeting,
            hasAttachments,
            isRecurring,
            isPrivate,
            isRecurringException,
            charmId,
            customStyles,
            showCompact,
            ...passThroughProps
        } = props;
        const details: Detail[] = [];
        const isCompactDensity =
            showCompact !== undefined ? showCompact : getDensityModeString() === 'compact';

        const charmProps: CharmControlColorProps = {
            colorPattern: CharmControlColorPattern.Normal,
            calPrimary: getPalette().neutralPrimary,
            textColor: getPalette().neutralPrimary,
        };

        details.push({
            getTextProps: () => ({
                text: subject,
                className: classNames(
                    styles.subject,
                    customStyles?.common.subject,
                    {
                        isCancelled: isCancelled,
                    },
                    isCompactDensity
                ),
            }),
            getCharmControllProps: charmId
                ? () => ({ iconId: charmId, className: styles.charm, colorProps: charmProps })
                : null,
        });
        if (location) {
            details.push({
                getTextProps: () => ({
                    text: location,
                    className: customStyles?.common.location,
                }),
            });
        }

        if (!location && isMeeting && organizerName) {
            details.push({
                getTextProps: () => ({
                    text: organizerName,
                    className: customStyles?.common.organizer,
                }),
            });
        }

        const timeDetails = [
            {
                getTextProps: () => ({
                    text: formatUserTime(startTime),
                    className: classNames(
                        styles.timePrimaryText,
                        customStyles?.common.time,
                        isCompactDensity
                    ),
                }),
            },
            {
                getTextProps: () => ({
                    text: getDurationString(startTime, endTime),
                    className: classNames(
                        styles.timeSecondaryText,
                        customStyles?.common.time,
                        isCompactDensity
                    ),
                }),
            },
        ];
        const topDetails = {
            timeDetails: timeDetails,
            details: details,
            icons: getCalendarEventIcons(
                hasAttachments,
                isRecurring,
                isPrivate,
                isRecurringException
            ),
        };

        return (
            <AgendaEventViewBase
                {...passThroughProps}
                customStyles={customStyles}
                topDetails={topDetails}
                className={className}
            />
        );
    });

    return AgendaEventViewNormal;
}

export const AgendaEventViewNormal = withNormalView(AgendaEventViewBase);
